import { type AgentDTO, getAgents } from '@cronos-app/api';
import type { ChainLayer, ExplicitAddress } from '@cronos-app/sdk';
import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/config/ui';
import { Fraction } from 'bi-fraction';

import { useGlobalChainLayer } from '../useGlobalChainLayer';

type PageParam =
  | undefined
  | {
      first: number;
      skip: number;
    };

export type useAgentsOptions = UseInfiniteQueryOptions<Agent[]> & {
  n?: number;
  isGraduated?: boolean | null;
  sortBy?: 'market_cap' | 'created_at';
};

export const useAgents = ({
  n = 10,
  isGraduated,
  sortBy,
  ...options
}: useAgentsOptions = {}) => {
  const chainLayer = useGlobalChainLayer();

  return useInfiniteQuery<Agent[]>({
    queryKey: [useAgents.queryKey, n, isGraduated, sortBy, chainLayer],
    queryFn: async ({ pageParam: _pageParam }) => {
      const pageParam: NonNullable<PageParam> = _pageParam || {
        first: n,
        skip: 0,
      };

      return fetchAgents({
        chainLayer,
        first: pageParam.first,
        skip: pageParam.skip,
        isGraduated,
        sortBy,
      });
    },
    getNextPageParam: (lastPage, allPages): PageParam => {
      if (lastPage.length < n) {
        // there is no next page available.
        return undefined;
      }

      return {
        first: n,
        skip: allPages.length * n,
      };
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: RefetchInterval.Fast,
    ...options,
  });
};
useAgents.queryKey = 'useAgents';

const fetchAgents = async ({
  chainLayer,
  first,
  skip,
  isGraduated,
  sortBy,
}: {
  chainLayer: ChainLayer;
  first: number;
  skip: number;
  isGraduated?: useAgentsOptions['isGraduated'];
  sortBy?: useAgentsOptions['sortBy'];
}): Promise<Agent[]> => {
  const graduatedArg =
    isGraduated === undefined || isGraduated === null
      ? [true, false]
      : [isGraduated];
  const agents = await getAgents(chainLayer, first, skip, graduatedArg, sortBy);
  return agents.content.map((agent) => new Agent(agent));
};

export class Agent {
  id: number;
  name: string;
  symbol: string;
  description: string;
  image_url: string;
  website: string;
  x_handle: string;
  telegram_handle: string;
  youtube_handle: string;
  market_cap: Fraction;
  price_change_1d: Fraction;
  usd_price_change_1d: Fraction;
  created_at: Date | null;
  is_graduated: boolean;
  creator_address: string;
  agent_token_address: ExplicitAddress | null;
  graduate_progress: Fraction;
  bonding_token_address: ExplicitAddress;
  agent_lp_token_address: ExplicitAddress | null;
  bonding_pair_address: ExplicitAddress;
  revision_at: Date | null;

  constructor(rawAgent: AgentDTO) {
    this.id = rawAgent.id;
    this.name = rawAgent.name;
    this.symbol = rawAgent.symbol;
    this.description = rawAgent.description ?? '';
    this.image_url = rawAgent.image_url ?? '';
    this.website = rawAgent.website_url ?? '';
    this.x_handle = rawAgent.xhandle ?? '';
    this.telegram_handle = rawAgent.telegram_handle ?? '';
    this.youtube_handle = rawAgent.youtube_handle ?? '';
    this.created_at = rawAgent.created_at
      ? new Date(rawAgent.created_at)
      : null;
    this.revision_at = rawAgent.revision_at
      ? new Date(rawAgent.revision_at)
      : null;
    this.is_graduated = !!rawAgent.agent_token_address;
    this.creator_address = rawAgent.creator_address ?? '';
    this.agent_token_address = rawAgent.agent_token_address;
    this.bonding_token_address = rawAgent.bonding_token_address;
    this.agent_lp_token_address = rawAgent.agent_lp_token_address;
    this.bonding_pair_address = rawAgent.bonding_pair_address;
    this.market_cap = new Fraction(rawAgent.market_cap_usd ?? 0);

    this.price_change_1d = new Fraction(rawAgent.price_change24h ?? 0);
    this.usd_price_change_1d = new Fraction(rawAgent.usd_price_change24h ?? 0);

    const maxGraduateProgress = rawAgent.agent_token_address ? 1 : 0.9999;
    this.graduate_progress = new Fraction(
      Math.min(maxGraduateProgress, rawAgent.graduation_progress),
    );
  }

  equals(other: Agent): boolean {
    return (
      this.id === other.id &&
      this.market_cap.eq(other.market_cap) &&
      this.agent_token_address === other.agent_token_address
    );
  }
}
